import React, { useState } from 'react'
import ReactGA from 'react-ga4'
import { useRouter } from 'next/router'
import { Checkbox, Button, Dropdown } from 'semantic-ui-react'

import RequestService from 'services/request.service'
import ToastService from 'services/toast.service'

import { HUMAN_GAME } from 'types/deck'

import { Spacer } from 'views_DEPRICATED/Pages/style'

import ProgressLoadingBar from 'components/elements/ProgressLoadingBar'
import Modal from 'components/elements/Modal'

import { humanReadableObjectToDropdown } from 'utils/DropdownConversions'

import styles from './exportModal.module.scss'

const PAGE_SIZE = 2500

type Props = { open: boolean; onClose: () => void }

const ExportModal = ({ open, onClose }: Props) => {
  const router = useRouter()

  const pageGame = Number(router.query.collectionGame) || 1

  const [game, setGame] = useState(pageGame)
  const [exporting, setExporting] = useState(false)
  const [totalImportPages, setTotalImportPages] = useState<number | null>(null)
  const [importPage, setImportPage] = useState(0)

  const [fields, setFields] = useState({
    quantity: true,
    card__oracleCard__name: true,
    modifier: true,
    condition: true,
    createdAt: true,
    language: true,
    purchasePrice: true,
    tags: true,
    card__edition__editionname: true,
    card__edition__editioncode: true,
    card__multiverseid: true,
    card__uid: true,
    card__mtgoNormalId: false,
    card__collectorNumber: true,
    card__cmc: false,
    card__color: false,
    card__colorIdentity: false,
    card__manaCost: false,
    card__types: false,
    card__subtypes: false,
    card__supertypes: false,
    card__rarity: false,
    card__prices__ck: false,
    card__prices__tcg: false,
    card__prices__scg: false,
    card__prices__mtgo: false,
    card__prices__cm: false,
    card__oracle__uid: false,
  })

  const toggle = (name: keyof typeof fields) => setFields({ ...fields, [name]: !fields[name] })

  const fetchCollectionPage = (fields: Array<string>, page = 1) => {
    // prettier-ignore
    if (!router.query.username) return Promise.reject('You are using an old client route for your export. Follow the link in the banner to export your collection.')

    return RequestService.post(`/api/collection/export/v2/${router.query.username}/`, {
      fields,
      page,
      game,
      pageSize: PAGE_SIZE,
    })
  }

  const handleSubmit = async () => {
    const fieldsList = []

    for (const field in fields) {
      if (fields[field as keyof typeof fields]) fieldsList.push(field)
    }

    // prettier-ignore
    if (!fieldsList.length) return ToastService.create('You must have at least one box checked to export', 'Collection Export')

    ReactGA.event({ category: 'Collection', action: 'Exporting the collection.' })

    let content = ''
    let page = 1
    let hasMore = true

    setExporting(true)

    try {
      while (hasMore) {
        const response = await fetchCollectionPage(fieldsList, page)

        setTotalImportPages(Math.ceil(response.totalRows / PAGE_SIZE))
        setImportPage(page)

        content += response.content

        page++
        hasMore = response.moreContent
      }

      const link = document.createElement('a')

      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(content)}`
      link.download = `archidekt-collection-export-${new Date().toISOString().substring(0, 10)}.csv`
      link.click()
    } catch (err: any) {
      if (err?.error) return ToastService.create(err.error, 'Collection Export')
      if (err?.details) return ToastService.create(err.details, 'Collection Export')

      ToastService.create(err, 'Collection Export')
    } finally {
      hasMore = false

      setImportPage(0)
      setTotalImportPages(null)
      setExporting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} closeButton>
      <h2 className={styles.header}>Export your Collection</h2>
      <div>
        <div className={styles.dropdown}>
          <label>Choose collection type</label>
          <Dropdown
            selection
            name="game"
            options={humanReadableObjectToDropdown(HUMAN_GAME)}
            onChange={(_e, { value }) => setGame(value as number)}
            value={game}
          />
        </div>

        <h3>Choose your CSV fields:</h3>
        <div className={styles.options}>
          <Checkbox label="Quantity" onChange={() => toggle('quantity')} checked={fields.quantity} />
          <Checkbox
            label="Card Name"
            onChange={() => toggle('card__oracleCard__name')}
            checked={fields.card__oracleCard__name}
          />
          <Checkbox label="Foil" onChange={() => toggle('modifier')} checked={fields.modifier} />
          <Checkbox label="Condition" onChange={() => toggle('condition')} checked={fields.condition} />
          <Checkbox label="Date Added" onChange={() => toggle('createdAt')} checked={fields.createdAt} />
          <Checkbox label="Language" onChange={() => toggle('language')} checked={fields.language} />
          <Checkbox label="Purchase price" onChange={() => toggle('purchasePrice')} checked={fields.purchasePrice} />
          <Checkbox label="Tags" onChange={() => toggle('tags')} checked={fields.tags} />
          <Checkbox
            label="Edition Name"
            onChange={() => toggle('card__edition__editionname')}
            checked={fields.card__edition__editionname}
          />
          <Checkbox
            label="Edition Code"
            onChange={() => toggle('card__edition__editioncode')}
            checked={fields.card__edition__editioncode}
          />
          <Checkbox
            label="Multiverse ID"
            onChange={() => toggle('card__multiverseid')}
            checked={fields.card__multiverseid}
          />
          <Checkbox
            label="Collector Number"
            onChange={() => toggle('card__collectorNumber')}
            checked={fields.card__collectorNumber}
          />
          <Checkbox label="Scryfall ID" onChange={() => toggle('card__uid')} checked={fields.card__uid} />
          <Checkbox label="MTGO ID" onChange={() => toggle('card__mtgoNormalId')} checked={fields.card__mtgoNormalId} />
          <Checkbox label="Mana value" onChange={() => toggle('card__cmc')} checked={fields.card__cmc} />
          <Checkbox label="Colors" onChange={() => toggle('card__color')} checked={fields.card__color} />
          <Checkbox
            label="Color identities"
            onChange={() => toggle('card__colorIdentity')}
            checked={fields.card__colorIdentity}
          />
          <Checkbox label="Mana cost" onChange={() => toggle('card__manaCost')} checked={fields.card__manaCost} />
          <Checkbox label="Card types" onChange={() => toggle('card__types')} checked={fields.card__types} />
          <Checkbox label="Sub-types" onChange={() => toggle('card__subtypes')} checked={fields.card__subtypes} />
          <Checkbox label="Super-types" onChange={() => toggle('card__supertypes')} checked={fields.card__supertypes} />
          <Checkbox label="Rarity" onChange={() => toggle('card__rarity')} checked={fields.card__rarity} />
          <Checkbox
            label="Price (Card Kingdom)"
            onChange={() => toggle('card__prices__ck')}
            checked={fields.card__prices__ck}
          />
          <Checkbox
            label="Price (TCG Player)"
            onChange={() => toggle('card__prices__tcg')}
            checked={fields.card__prices__tcg}
          />
          <Checkbox
            label="Price (Star City Games)"
            onChange={() => toggle('card__prices__scg')}
            checked={fields.card__prices__scg}
          />
          <Checkbox
            label="Price (Card Market)"
            onChange={() => toggle('card__prices__cm')}
            checked={fields.card__prices__cm}
          />
          <Checkbox
            label="Price (Card Hoarder)"
            onChange={() => toggle('card__prices__mtgo')}
            checked={fields.card__prices__mtgo}
          />
          <Checkbox label="Oracle ID" onChange={() => toggle('card__oracle__uid')} checked={fields.card__oracle__uid} />
        </div>
        <Spacer height={15} />
        <Button onClick={handleSubmit} color="blue" disabled={exporting}>
          Export
        </Button>

        {exporting && (
          <ProgressLoadingBar
            label="Exporting collection"
            completionPercentage={importPage / (totalImportPages || 1)}
          />
        )}
      </div>
    </Modal>
  )
}

export default ExportModal
